/* eslint-disable curly */
const BASE = "/api";

export type CanvasType = "canvas" | "notebook";

export type NotebookPageStyle = "blank" | "ruled" | "grid" | "dotted";

export interface Folder {
  id: string;
  parent_id: string | null;
  name: string;
  position: number;
  created_at: number;
  updated_at: number;
}

export interface CanvasMeta {
  id: string;
  folder_id: string | null;
  name: string;
  description: string | null;
  position: number;
  created_at: number;
  updated_at: number;
  tags: string[];
  canvas_type: CanvasType;
  page_style: NotebookPageStyle | null;
}

export interface CanvasScene {
  type: string;
  version: number;
  source: string;
  elements: unknown[];
  appState: Record<string, unknown>;
  files: Record<string, unknown>;
}

export interface Tag {
  id: string;
  name: string;
  color: string | null;
}

export interface SharePayload {
  token: string;
  name: string | null;
  description: string | null;
  created_at: number;
}

export interface SharedScene {
  token: string;
  name: string | null;
  description: string | null;
  created_at: number;
  view_count: number;
  scene: CanvasScene;
}

export interface AuthUser {
  id: string;
  username: string;
  role: "admin" | "user";
}

export interface AuthStatus {
  needsSetup: boolean;
  authenticated: boolean;
  user: AuthUser | null;
}

export interface ManagedUser {
  id: string;
  username: string;
  role: "admin" | "user";
  created_at: number;
}

export interface BackupBundle {
  version: number;
  app: string;
  exportedAt: number;
  folders: {
    id: string;
    parent_id: string | null;
    name: string;
    position: number;
  }[];
  canvases: {
    id: string;
    folder_id: string | null;
    name: string;
    description: string | null;
    position: number;
    scene: CanvasScene;
    canvas_type: CanvasType;
    page_style: NotebookPageStyle | null;
  }[];
  tags: { id: string; name: string; color: string | null }[];
  canvasTags: { canvas_id: string; tag_id: string }[];
}

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const r = await fetch(BASE + url, {
    ...init,
    // Send the session cookie (same-origin in dev via Vite proxy and in prod).
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers || {}),
    },
  });
  if (!r.ok) {
    let detail = "";
    try {
      detail = await r.text();
    } catch {
      /* ignore */
    }
    throw new Error(`${r.status} ${r.statusText}: ${detail}`);
  }
  if (r.status === 204) return undefined as T;
  return r.json();
}

export const libraryApi = {
  listFolders: () => req<Folder[]>("/folders"),
  createFolder: (body: {
    name: string;
    parent_id?: string | null;
    id?: string;
    position?: number;
  }) => req<Folder>("/folders", { method: "POST", body: JSON.stringify(body) }),
  updateFolder: (
    id: string,
    body: Partial<Pick<Folder, "name" | "parent_id" | "position">>,
  ) =>
    req<Folder>(`/folders/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteFolder: (id: string) =>
    req<void>(`/folders/${id}`, { method: "DELETE" }),

  listCanvases: (folder_id?: string | null) => {
    const q =
      folder_id === undefined
        ? ""
        : `?folder_id=${folder_id === null ? "null" : folder_id}`;
    return req<CanvasMeta[]>(`/canvases${q}`);
  },
  getCanvas: (id: string) => req<CanvasMeta>(`/canvases/${id}`),
  createCanvas: (body: {
    name: string;
    folder_id?: string | null;
    scene?: unknown;
    id?: string;
    position?: number;
    canvas_type?: CanvasType;
    page_style?: NotebookPageStyle;
  }) =>
    req<CanvasMeta>("/canvases", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateCanvas: (
    id: string,
    body: Partial<{
      name: string;
      folder_id: string | null;
      position: number;
      description: string | null;
      canvas_type: CanvasType;
      page_style: NotebookPageStyle | null;
    }>,
  ) =>
    req<CanvasMeta>(`/canvases/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteCanvas: (id: string) =>
    req<void>(`/canvases/${id}`, { method: "DELETE" }),
  getScene: (id: string) => req<CanvasScene>(`/canvases/${id}/scene`),
  saveScene: (id: string, scene: unknown) =>
    req<{ ok: boolean; updated_at: number }>(`/canvases/${id}/scene`, {
      method: "PUT",
      body: JSON.stringify(scene),
    }),

  listTags: () => req<Tag[]>("/tags"),
  createTag: (body: { name: string; color?: string | null }) =>
    req<Tag>("/tags", { method: "POST", body: JSON.stringify(body) }),
  deleteTag: (id: string) => req<void>(`/tags/${id}`, { method: "DELETE" }),
  addTagToCanvas: (canvasId: string, tagId: string) =>
    req<void>(`/canvases/${canvasId}/tags`, {
      method: "POST",
      body: JSON.stringify({ tag_id: tagId }),
    }),
  removeTagFromCanvas: (canvasId: string, tagId: string) =>
    req<void>(`/canvases/${canvasId}/tags/${tagId}`, { method: "DELETE" }),

  createShare: (body: {
    scene: unknown;
    name?: string | null;
    canvas_id?: string | null;
    description?: string | null;
  }) =>
    req<SharePayload>("/shares", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getShare: (token: string) => req<SharedScene>(`/shares/${token}`),
  getShareForCanvas: (canvasId: string) =>
    req<{
      token: string;
      name: string | null;
      description: string | null;
      created_at: number;
      view_count: number;
    }>(`/shares/by-canvas/${canvasId}`),
  revokeShare: (token: string) =>
    req<void>(`/shares/${token}`, { method: "DELETE" }),

  // --- Auth ---
  authStatus: () => req<AuthStatus>("/auth/status"),
  setup: (body: { username: string; password: string }) =>
    req<{ user: AuthUser }>("/auth/setup", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  login: (body: { username: string; password: string }) =>
    req<{ user: AuthUser }>("/auth/login", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  logout: () => req<void>("/auth/logout", { method: "POST" }),

  // --- User management (admin) ---
  listUsers: () => req<ManagedUser[]>("/users"),
  createUser: (body: {
    username: string;
    password: string;
    role: "admin" | "user";
  }) =>
    req<ManagedUser>("/users", { method: "POST", body: JSON.stringify(body) }),
  updateUser: (
    id: string,
    body: { password?: string; role?: "admin" | "user" },
  ) =>
    req<ManagedUser>(`/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteUser: (id: string) => req<void>(`/users/${id}`, { method: "DELETE" }),

  // --- Backup / restore ---
  exportBackup: () => req<BackupBundle>("/backup"),
  importBackup: (bundle: BackupBundle) =>
    req<{ folders: number; canvases: number }>("/backup/import", {
      method: "POST",
      body: JSON.stringify(bundle),
    }),
};
